import {
    defaultTopMenuOptions,
    type TopMenuOptions,
    useAdsStore,
    useFooterStore,
    useMiniChatStore,
    useTopMenuStore,
} from '@frontend/stores'
import {
    type DependencyList,
    useEffect,
} from 'react'

export interface UiConfigTopMenuConfig extends TopMenuOptions {
    visible: boolean
}

export interface UiConfigFooterConfig {
    visible: boolean
}

// Настройки глобальных элементов интерфейса приложения
export interface UiConfig {
    // Настройка верхнего меню навигации.
    topMenu?: UiConfigTopMenuConfig
    // Видимость панели поиска объявлений.
    adsSearch?: boolean
    // Видимость подвала и панели навигации в нем.
    footer?: UiConfigFooterConfig
    // Видимость мини-чата. По умолчанию: true.
    miniChat?: boolean
    // Другие кастомные настройки для выполнения в useEffect().
    other?: () => void
    // Выполнить при демонтаже компонента.
    onUnmount?: () => void
}

type DefaultUiConfig = Required<Omit<UiConfig, 'other' | 'onUnmount'>> & Pick<UiConfig, 'other' | 'onUnmount'>

function getDefaults(): DefaultUiConfig {
    return {
        topMenu: {
            visible: true,
            ...defaultTopMenuOptions,
        },
        adsSearch: true,
        footer: {
            visible: true,
        },
        miniChat: true,
    }
}

// Настройка глобальных элементов интерфейса приложения.
export function useConfigureUi(
    // В config заданы значения по умолчанию.
    // В нормальном случае нужно изменить config и вернуть его.
    // Если вернуть null, то UI останется без изменений.
    getConfig: (config: DefaultUiConfig) => UiConfig | null = config => config,
    deps: DependencyList = []
) {
    const setAdsSearchVisibility = useAdsStore(
        state => state.setSearchPanelVisibility
    )
    const setFooterVisibility = useFooterStore(
        state => state.setVisibility
    )
    const setTopMenuVisibility = useTopMenuStore(
        state => state.setVisibility
    )
    const setMiniChatVisibility = useMiniChatStore(
        state => state.setVisibility
    )

    useEffect(() => {
        const config = getConfig(getDefaults())
        if (!config) {
            return
        }
        // Мини-чат.
        const {
            miniChat = true,
        } = config
        setMiniChatVisibility(miniChat)
        // Верхнее меню навигации.
        if (config.topMenu !== undefined) {
            const {
                visible,
                ...topMenuOptions
            } = config.topMenu
            setTopMenuVisibility(visible, topMenuOptions)
        }
        // Панель поиска.
        if (config.adsSearch !== undefined) {
            setAdsSearchVisibility(config.adsSearch)
        }
        // Подвал.
        if (config.footer !== undefined) {
            setFooterVisibility(config.footer.visible)
        }
        // Другое.
        config.other?.()

        // Демонтаж.
        return config.onUnmount
    }, deps)
}
